// @ts-strict
import {createContext, type ReactElement, useContext, useEffect, useRef, useState} from 'react';
import {Formik, useFormikContext} from 'formik';
import useTranslation from 'next-translate/useTranslation';

import useIsTabletOrDesktopCheck from '@/hooks/useIsTabletOrDesktopCheck';
import useTracking from '@/hooks/useTracking';
import {
  type FieldContext,
  StepEnum,
  type SubmitResult,
  type SupportCaseInput,
  type SupportMessageSource,
  type SupportMessageSurface,
  type WebToCaseFormValues,
} from './types';
import {
  buildSupportCaseInput,
  getActiveCategoryLevel2,
  getInitialValues,
  getSubmitErrorKey,
  stepForFieldErrors,
  toFormikErrors,
  validateSupportMessage,
} from './utils';

// Formik reads initialValues once, but useIdentity resolves later, so prefill is applied on arrival
// — into untouched fields only, so it never overwrites typing.
const PrefillWhenIdentityArrives = ({fullName, email}: FieldContext): null => {
  const {setFieldValue, values} = useFormikContext<WebToCaseFormValues>();
  const applied = useRef(false);

  useEffect(() => {
    if (applied.current || (!fullName && !email)) return;
    applied.current = true;

    if (fullName && values.contactName === '') setFieldValue('contactName', fullName, false);
    if (email && values.email === '') setFieldValue('email', email, false);
  }, [fullName, email, setFieldValue, values.contactName, values.email]);

  return null;
};

type SupportMessageWizardValue = {
  step: StepEnum;
  setStep: (step: StepEnum) => void;
  isSingleStep: boolean;
  submitErrorKey: string | null;
  surface: SupportMessageSurface;
  trackingContext: {surface: SupportMessageSurface; source?: SupportMessageSource};
};

const SupportMessageWizardContext = createContext<SupportMessageWizardValue | undefined>(undefined);

export const useSupportMessageWizard = (): SupportMessageWizardValue => {
  const context = useContext(SupportMessageWizardContext);
  if (!context)
    throw new Error('useSupportMessageWizard must be used within a SupportMessageProvider');
  return context;
};

export type SupportMessageProviderProps = {
  children: React.ReactNode;
  surface: SupportMessageSurface;
  fieldContext: FieldContext;
  onSubmit: (input: SupportCaseInput) => Promise<SubmitResult>;
  onSuccess: () => void;
  source?: SupportMessageSource;
};

const SupportMessageProvider = ({
  children,
  surface,
  fieldContext,
  onSubmit,
  onSuccess,
  source,
}: SupportMessageProviderProps): ReactElement => {
  const {t} = useTranslation('common');
  const {track} = useTracking();
  const [step, setStep] = useState<StepEnum>(StepEnum.ContactAndCategory);
  const [submitErrorKey, setSubmitErrorKey] = useState<string | null>(null);

  // Mobile shows every field on one screen. Checked this way round so the single-step layout is
  // what renders before the media query resolves — the standalone route is always mobile.
  const isSingleStep = !useIsTabletOrDesktopCheck();

  const trackingContext = {surface, ...(source ? {source} : {})};

  return (
    <Formik
      initialValues={getInitialValues(fieldContext)}
      validate={values => validateSupportMessage(values, t)}
      validateOnChange={false}
      validateOnBlur
      onSubmit={async (values, {setErrors, setSubmitting}) => {
        setSubmitErrorKey(null);
        const result = await onSubmit(buildSupportCaseInput(values, source ?? surface));
        setSubmitting(false);

        if (result.success) {
          track('support-message-submitted', {
            page: window.location.pathname,
            misc_json: JSON.stringify({
              ...trackingContext,
              category_1: values.categoryLevel1,
              category_2: getActiveCategoryLevel2(values),
            }),
          });
          onSuccess();
          return;
        }

        if (result.fieldErrors) {
          setErrors(toFormikErrors(result.fieldErrors, values));
          const targetStep = stepForFieldErrors(result.fieldErrors);
          if (targetStep) setStep(targetStep);
          return;
        }

        track('support-message-submit-failed', {
          page: window.location.pathname,
          misc_json: JSON.stringify({
            ...trackingContext,
            reason: result.reason ?? 'network_error',
          }),
        });
        setSubmitErrorKey(getSubmitErrorKey(result.reason));
      }}
    >
      <SupportMessageWizardContext.Provider
        value={{step, setStep, isSingleStep, submitErrorKey, surface, trackingContext}}
      >
        <PrefillWhenIdentityArrives {...fieldContext} />
        {children}
      </SupportMessageWizardContext.Provider>
    </Formik>
  );
};

export default SupportMessageProvider;
